import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Package, Loader2, CornerDownLeft } from "lucide-react";
import { fmtTL } from "@/components/StatusBadge";

// F12 / dürbün ürün arama penceresi — Ad/SKU ile ara, ↑↓ gez, Enter ile seç (muhasebe programı tarzı).
export default function ProductPickerModal({ open, onClose, onPick, initialQuery = "" }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) { setQ(initialQuery || ""); setActive(0); setTimeout(() => inputRef.current?.focus(), 60); }
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products?q=${encodeURIComponent(q.trim())}&page_size=25&channel=retail&match=contains`);
        setRes((data.items || []).slice(0, 25)); setActive(0);
      } catch { setRes([]); } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, res]);

  const choose = (p) => { if (p) { onPick(p); onClose(); } };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, res.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(res[active]); }
    else if (e.key === "Escape") { onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="product-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Search className="h-4 w-4 text-emerald-700" /> Ürün Ara — Ad / Stok Kodu
            <span className="ml-auto text-[11px] font-normal text-slate-400">↑↓ gezin · Enter seç · Esc kapat</span>
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input ref={inputRef} data-testid="product-picker-search" value={q} onKeyDown={onKey}
            onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı veya SKU yazın…" className="h-11 pl-9 text-base" />
        </div>
        <div ref={listRef} data-testid="product-picker-list" className="mt-2 max-h-[52vh] divide-y divide-slate-100 overflow-y-auto overscroll-contain rounded-lg border border-slate-200">
          {loading && <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Aranıyor…</div>}
          {!loading && res.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Eşleşen ürün bulunamadı</div>}
          {!loading && res.map((p, idx) => (
            <button key={p.id} data-idx={idx} data-testid={`product-picker-opt-${p.id}`} type="button"
              onMouseEnter={() => setActive(idx)} onClick={() => choose(p)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${idx === active ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
              <Package className={`h-4 w-4 shrink-0 ${idx === active ? "text-emerald-700" : "text-slate-400"}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">{p.name}</p>
                <p className="truncate font-mono text-[11px] text-slate-400">{p.sku || "—"} · {p.base_unit || "Adet"} · %{p.vat_rate ?? 20} KDV</p>
              </div>
              <span className="shrink-0 font-mono text-sm font-semibold text-emerald-800">{fmtTL(Number(p?.sales_prices?.market_retail) || 0)}</span>
              {idx === active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
